import { routeEstimateResponseSchema } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type GeoLocation, useDeviceGeoLocationIfAllowed } from "@src/appV2/Location";
import {
  type QueryClient,
  type QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";
import { isFinite, round } from "lodash";
import { useEffect } from "react";

import { computeArrivalData, type ComputedArrivalData } from "../computeArrivalData";
import { useArrivalTimeConfig } from "../useArrivalTimeConfig";

interface UseShiftRouteEstimateParams {
  destination?: GeoLocation;
  workerId: string;
  shiftId?: string;
  shiftInviteId?: string;
  workplaceId: string;
  shiftStartIso?: string;
}

export const GET_ROUTE_ESTIMATES_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/route-estimates`;

interface GetQueryKeyForShiftRouteEstimateParams {
  shiftOrShiftInviteId: string | undefined;
  source: GeoLocation;
}

function roundCoordinates(coordinates: GeoLocation): GeoLocation {
  return {
    latitude: round(coordinates.latitude, 4),
    longitude: round(coordinates.longitude, 4),
  };
}

function getQueryKeyForShiftRouteEstimate(
  params: GetQueryKeyForShiftRouteEstimateParams
): QueryKey {
  const { shiftOrShiftInviteId, source } = params;
  return ["shiftRouteEstimate", shiftOrShiftInviteId, source];
}

export function getCachedShiftRouteEstimate(
  params: GetQueryKeyForShiftRouteEstimateParams & { queryClient: QueryClient }
): ComputedArrivalData | undefined {
  const { queryClient, shiftOrShiftInviteId, source } = params;

  const roundedSourceCoordinates = roundCoordinates(source);

  return queryClient.getQueryData<ComputedArrivalData>(
    getQueryKeyForShiftRouteEstimate({ shiftOrShiftInviteId, source: roundedSourceCoordinates })
  );
}

/**
 * Gets cached shift route estimate
 */
export function useGetCachedShiftRouteEstimate(shiftOrShiftInviteId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: deviceGeoLocation } = useDeviceGeoLocationIfAllowed();
  const source = deviceGeoLocation?.geoLocation;

  return isDefined(shiftOrShiftInviteId) && isDefined(source)
    ? getCachedShiftRouteEstimate({
        queryClient,
        shiftOrShiftInviteId,
        source,
      })
    : undefined;
}

/**
 * This hook fetches worker location and gets the route estimate and computing arrival data.
 * It handles logging for missing data as well.
 */

export function useShiftRouteEstimate(
  params: UseShiftRouteEstimateParams,
  options: UseQueryOptions<ComputedArrivalData> = {}
): UseQueryResult<ComputedArrivalData> {
  const { destination, workerId, shiftId, workplaceId, shiftInviteId, shiftStartIso } = params;

  // We assume that by this point, the user has granted permission to access their location
  const { data: deviceGeoLocation } = useDeviceGeoLocationIfAllowed();
  const source = deviceGeoLocation?.geoLocation;

  const shiftOrShiftInviteId = shiftId ?? shiftInviteId;

  const isSourceDefined =
    isDefined(source) && isFinite(source.longitude) && isFinite(source.latitude);

  const isDestinationDefined =
    isDefined(destination) && isFinite(destination.longitude) && isFinite(destination.latitude);

  const roundedSourceCoordinates = isDefined(source) ? roundCoordinates(source) : undefined;
  const { longitude: workerLongitude, latitude: workerLatitude } = roundedSourceCoordinates ?? {};

  const { gracePeriodInMinutesForDriveETA } = useArrivalTimeConfig();

  useEffect(() => {
    // some of the data might be missing because it hasn't been fetched in the upper component yet.
    // It should pass 'enabled' as false in these cases, so we're filtering it out here.
    if (!options.enabled) {
      return;
    }

    if (!isSourceDefined) {
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "Worker location is missing",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
      });
      return;
    }

    if (!isDestinationDefined) {
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "Destination is missing",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
        workerLatitude,
        workerLongitude,
      });
    }
  }, [
    isSourceDefined,
    isDestinationDefined,
    options.enabled,
    workerId,
    shiftId,
    shiftInviteId,
    workplaceId,
    workerLatitude,
    workerLongitude,
  ]);

  return useQuery<ComputedArrivalData, Error, ComputedArrivalData, any>({
    queryKey: getQueryKeyForShiftRouteEstimate({
      shiftOrShiftInviteId,
      source: roundedSourceCoordinates ?? { longitude: 0, latitude: 0 },
    }),
    queryFn: async () => {
      const response = await get({
        url: GET_ROUTE_ESTIMATES_URL,
        queryParams: {
          source: {
            coordinates: [workerLongitude, workerLatitude],
          },
          destination: {
            coordinates: [destination?.longitude, destination?.latitude],
          },
        },
        responseSchema: routeEstimateResponseSchema,
      });

      // We deliberately convert the route estimate to computed arrival data here instead
      // of a select clause so we can easily access it via the query cache
      return computeArrivalData({
        routeEstimate: response.data,
        shiftStartIso: shiftStartIso ?? "",
        gracePeriodInMinutes: gracePeriodInMinutesForDriveETA,
      });
    },
    onError: () => {
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "No ETA available from the server",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
        workerLatitude,
        workerLongitude,
      });
    },
    staleTime: minutesToMilliseconds(5),
    ...options,
    enabled:
      isSourceDefined &&
      isDestinationDefined &&
      isDefined(shiftOrShiftInviteId) &&
      isDefined(shiftStartIso) &&
      isDefined(gracePeriodInMinutesForDriveETA) &&
      options.enabled !== false,
  });
}
